import {
  type AdminBookListResponse,
  type AdminPodcastCreate,
  type AdminPodcastListResponse,
  type AdminUserAnalyticsRefreshRequest,
  type AdminUserAnalyticsRefreshResponse,
  type AdminUserAnalyticsRefreshStatus,
  type AdminUserCreate,
  type AdminUserUpdate,
  type BulkImportResponse,
  type DashboardStats,
  type GenerateAllBooksResponse,
  type GenerateAllPodcastsResponse,
  type GenerateBookResponse,
  type GeneratePodcastResponse,
  type Organization,
  type OrganizationCreate,
  type OrganizationListParams,
  type OrganizationListResponse,
  type OrganizationUpdate,
  type User,
  type UserListParams,
  type UserListResponse,
  clearSafetyFlag,
  createAdminPodcast,
  createOrganization,
  createUser,
  deleteOrganization,
  deleteUser,
  generateAllBookContent,
  generateAllPodcastContent,
  generateBookContent,
  generatePodcastContent,
  generatePodcastCover,
  getAdminBooks,
  getAdminPodcasts,
  getOrganization,
  getOrganizationUsers,
  getOrganizations,
  getSafetyFlaggedUsers,
  getStats,
  getUser,
  getUserAnalyticsRefreshStatus,
  getUsers,
  importUsers,
  refreshUserAnalytics,
  updateOrganization,
  updateUser,
} from '@/services/admin.service'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// Query keys
export const adminKeys = {
  all: ['admin'] as const,
  users: () => [...adminKeys.all, 'users'] as const,
  userList: (params: UserListParams) => [...adminKeys.users(), 'list', params] as const,
  user: (id: number) => [...adminKeys.users(), 'detail', id] as const,
  stats: () => [...adminKeys.all, 'stats'] as const,
  organizations: () => [...adminKeys.all, 'organizations'] as const,
  organizationList: (params: OrganizationListParams) =>
    [...adminKeys.organizations(), 'list', params] as const,
  organization: (id: number) => [...adminKeys.organizations(), 'detail', id] as const,
  organizationUsers: (id: number, params: { page?: number; limit?: number }) =>
    [...adminKeys.organizations(), 'users', id, params] as const,
  safetyFlagged: () => [...adminKeys.all, 'safetyFlagged'] as const,
  books: () => [...adminKeys.all, 'books'] as const,
  podcasts: () => [...adminKeys.all, 'podcasts'] as const,
  userAnalyticsRefreshStatus: (jobId: string) =>
    [...adminKeys.users(), 'analytics-refresh', jobId] as const,
}

// Hooks
export function useUsers(params: UserListParams = {}) {
  return useQuery<UserListResponse>({
    queryKey: adminKeys.userList(params),
    queryFn: () => getUsers(params),
  })
}

export function useUser(id: number) {
  return useQuery<User>({
    queryKey: adminKeys.user(id),
    queryFn: () => getUser(id),
    enabled: !!id,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AdminUserCreate) => createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      queryClient.invalidateQueries({ queryKey: adminKeys.stats() })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AdminUserUpdate }) => updateUser(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      queryClient.invalidateQueries({ queryKey: adminKeys.user(id) })
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      queryClient.invalidateQueries({ queryKey: adminKeys.stats() })
    },
  })
}

export function useImportUsers() {
  const queryClient = useQueryClient()

  return useMutation<BulkImportResponse, Error, File>({
    mutationFn: (file: File) => importUsers(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      queryClient.invalidateQueries({ queryKey: adminKeys.stats() })
    },
  })
}

export function useStats() {
  return useQuery<DashboardStats>({
    queryKey: adminKeys.stats(),
    queryFn: () => getStats(),
  })
}

export function useRefreshUserAnalytics() {
  const queryClient = useQueryClient()
  return useMutation<AdminUserAnalyticsRefreshResponse, Error, AdminUserAnalyticsRefreshRequest>({
    mutationFn: (data) => refreshUserAnalytics(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useUserAnalyticsRefreshStatus(jobId: string | null) {
  return useQuery<AdminUserAnalyticsRefreshStatus>({
    queryKey: adminKeys.userAnalyticsRefreshStatus(jobId || 'none'),
    queryFn: () => getUserAnalyticsRefreshStatus(jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const current = query.state.data
      return current?.status === 'running' ? 2000 : false
    },
  })
}

// Safety flag hooks
export function useSafetyFlaggedUsers() {
  return useQuery<User[]>({
    queryKey: adminKeys.safetyFlagged(),
    queryFn: () => getSafetyFlaggedUsers(),
  })
}

export function useClearSafetyFlag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: number) => clearSafetyFlag(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.safetyFlagged() })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

// Organization hooks
export function useOrganizations(params: OrganizationListParams = {}) {
  return useQuery<OrganizationListResponse>({
    queryKey: adminKeys.organizationList(params),
    queryFn: () => getOrganizations(params),
  })
}

export function useOrganization(id: number) {
  return useQuery<Organization>({
    queryKey: adminKeys.organization(id),
    queryFn: () => getOrganization(id),
    enabled: !!id,
  })
}

export function useOrganizationUsers(id: number, params: { page?: number; limit?: number } = {}) {
  return useQuery<UserListResponse>({
    queryKey: adminKeys.organizationUsers(id, params),
    queryFn: () => getOrganizationUsers(id, params),
    enabled: !!id,
  })
}

export function useCreateOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: OrganizationCreate) => createOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.organizations() })
    },
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: OrganizationUpdate }) =>
      updateOrganization(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.organizations() })
      queryClient.invalidateQueries({ queryKey: adminKeys.organization(id) })
    },
  })
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => deleteOrganization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.organizations() })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

// Book admin hooks
export function useAdminBooks() {
  return useQuery<AdminBookListResponse>({
    queryKey: adminKeys.books(),
    queryFn: () => getAdminBooks(),
  })
}

export function useGenerateBookContent() {
  const queryClient = useQueryClient()

  return useMutation<GenerateBookResponse, Error, number>({
    mutationFn: (bookId: number) => generateBookContent(bookId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.books() })
    },
  })
}

export function useGenerateAllBookContent() {
  const queryClient = useQueryClient()

  return useMutation<GenerateAllBooksResponse, Error>({
    mutationFn: () => generateAllBookContent(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.books() })
    },
  })
}

// Podcast admin hooks
export function useAdminPodcasts() {
  return useQuery<AdminPodcastListResponse>({
    queryKey: adminKeys.podcasts(),
    queryFn: () => getAdminPodcasts(),
  })
}

export function useCreatePodcast() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AdminPodcastCreate) => createAdminPodcast(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.podcasts() })
    },
  })
}

export function useGeneratePodcastContent() {
  const queryClient = useQueryClient()

  return useMutation<GeneratePodcastResponse, Error, number>({
    mutationFn: (podcastId: number) => generatePodcastContent(podcastId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.podcasts() })
    },
  })
}

export function useGenerateAllPodcastContent() {
  const queryClient = useQueryClient()

  return useMutation<GenerateAllPodcastsResponse, Error>({
    mutationFn: () => generateAllPodcastContent(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.podcasts() })
    },
  })
}

export function useGeneratePodcastCover() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (podcastId: number) => generatePodcastCover(podcastId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.podcasts() })
    },
  })
}
